import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Icon, InfoIconTooltip, Input } from '@components';
import AmountInput from '@components/amount-input/AmountInput';
import AttendancePaymentLinkInfo from '@components/attendance-payment-link-info/AttendancePaymentLinkInfo';
import MonthCard from '@components/payment-collection/components/MonthCard';
import RadioButton from '@components/radio-button/RadioButton';
import { SPLIT_REQUIRED_AMOUNT_MIN } from '@constants/generic';
import { PaymentCollectionType } from '@features/group-management/constants';
import useGroupEndDate from '@features/group-management/hooks/useGroupEndDate';
import Modal from '@layouts/modal/Modal.v2';
import { FormDataType } from '@types';

import theme from '../../../../../../themes/colors';
import AttendanceSection from '../../v1/attendance-section/AttendanceSection';
import InstallmentSection from '../../v1/feeDetailsInput/InstallmentSection';
import GroupEndDateSelection from '../../v1/group-end-date-selection/GroupEndDateSelection';

interface AttendanceBasedFeeSectionProps {
  formData: FormDataType;
  onInputChange: (field: string, value: any) => void;
  isEditGroup?: boolean;
}

type PaymentRequestType = 'monthly' | 'session_based';

const getMonthDayLabel = (activationDate: string): string => {
  if (activationDate === 'first_day') return '1st day of the month';
  if (activationDate === 'last_day') return 'Last day of the month';
  if (activationDate && !Number.isNaN(Number(activationDate))) {
    const n = Number(activationDate);
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    const suffix = s[(v - 20) % 10] || s[v] || s[0];
    return `${n}${suffix} day of the month`;
  }
  return '';
};

const AttendanceBasedFeeSection = ({
  formData,
  onInputChange,
  isEditGroup = false
}: AttendanceBasedFeeSectionProps) => {
  const { orgConfig } = useSelector((state: any) => state.user);
  const splitRequiredMinAmount =
    orgConfig?.split_required_amount_min || SPLIT_REQUIRED_AMOUNT_MIN;

  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [tempActivationDate, setTempActivationDate] = useState<string>('');

  const {
    isEndDateModalOpen,
    setIsEndDateModalOpen,
    endDateMinDate,
    endDateSelectedDate,
    onEndDateChange,
    onClearEndDate
  } = useGroupEndDate({ formData, onInputChange });

  const selectedMonthLabel = getMonthDayLabel(formData.activationDate);

  const handlePaymentRequestTypeChange = (type: PaymentRequestType) => {
    if (type === 'monthly') {
      onInputChange('paymentCollectionType', PaymentCollectionType.MONTHLY);
      onInputChange('intervalFrequency', '1');
      if (formData.paymentCollectionType !== PaymentCollectionType.MONTHLY) {
        onInputChange('activationDate', '');
      }
      onInputChange('sessionsPerCycle', '');
    } else if (type === 'session_based') {
      onInputChange(
        'paymentCollectionType',
        PaymentCollectionType.SESSION_BASED
      );
      onInputChange('intervalFrequency', '1');
      onInputChange('activationDate', '');
    }
  };

  const handleOpenBillingModal = () => {
    setTempActivationDate(formData.activationDate || '');
    setIsBillingModalOpen(true);
  };

  const handleBillingModalApply = () => {
    onInputChange('activationDate', tempActivationDate);
    onInputChange('intervalFrequency', '1');
    setIsBillingModalOpen(false);
  };

  const handleBillingModalCancel = () => {
    setTempActivationDate('');
    setIsBillingModalOpen(false);
  };

  const handleSessionsChange = (value: string) => {
    const num = value ? Number(value) : undefined;
    onInputChange('sessionsPerCycle', num);
  };

  const [amountError, setAmountError] = useState<React.ReactNode>('');

  const handleAmountChange = (value: string) => {
    setAmountError('');
    onInputChange('amount', value);
    if (
      Number(value) < Number(splitRequiredMinAmount) &&
      formData?.paymentInstallment
    ) {
      onInputChange('paymentInstallment', false);
    }
  };

  const handleAmountBlur = (value: string) => {
    if (
      value &&
      Number(value) !== 0 &&
      (!Number.isFinite(Number(value)) || Number(value) < 2)
    ) {
      setAmountError(
        <span>
          Minimum rate is <span className="font-inter">₹</span>2
        </span>
      );
    }
  };

  const isMonthly =
    formData.paymentCollectionType === PaymentCollectionType.MONTHLY;
  const isSessionBased =
    formData.paymentCollectionType === PaymentCollectionType.SESSION_BASED;

  return (
    <div className="rounded-xl border border-cofeeGreyBlue200 bg-cofeeNeutral1 py-4">
      <h3 className="border-b border-cofeeGreyBlue200 px-6 pb-4 text-lg font-semibold text-cofeeNeutral10">
        Attendance-based fee details
      </h3>
      <div className="grid gap-y-6 p-6">
        {/* Working Days + Notify absentees */}
        <AttendanceSection
          onInputChange={onInputChange}
          workingDays={formData.workingDays}
          isSmsAlertEnabled={formData.isSendAttendanceAlertEnabled}
          className="!mt-0"
          dayOptionClassName="!bg-white"
          required
        />

        {/* Divider */}
        <hr className="border-cofeeGreyBlue100" />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Rate per session */}
          <div>
            <label className="mb-1 block text-sm font-semibold text-cofeeNeutral10">
              Rate per class <span className="text-error">*</span>
            </label>
            <AmountInput
              name="rate-per-session"
              value={formData.amount}
              onAmountChange={handleAmountChange}
              handleInputBlur={handleAmountBlur}
              placeholder="0"
              suffixText="/ Class"
              allowDecimals
              allowZero
              error={!!amountError}
              errorMessage={amountError}
            />
            <div
              className={`${
                amountError ? 'mt-6' : 'mt-1.5'
              } flex items-center gap-1 text-xs text-cofeeNeutral7`}
            >
              <InfoIconTooltip
                tooltipText=""
                id="rate-per-session-info"
                iconClassName="!w-5 !h-5"
              />
              {/* currency Amount */}
              <div className="flex items-center gap-0.5">
                <span>Enter the amount to charge per class attended</span>
              </div>
            </div>
          </div>
          <GroupEndDateSelection
            groupEndDateClassName="!w-full"
            labelClassName="!mb-1"
            formData={formData}
            onClearEndDate={onClearEndDate}
            setIsEndDateModalOpen={setIsEndDateModalOpen}
            isEndDateModalOpen={isEndDateModalOpen}
            onEndDateChange={onEndDateChange}
            endDateMinDate={endDateMinDate}
            endDateSelectedDate={endDateSelectedDate}
            isEndDateDisabled={false}
          />
        </div>

        {/* When should payment be requested? */}
        <div>
          <p className="mb-3 font-semibold text-cofeeNeutral10">
            When should payment links be sent?{' '}
            <span className="text-error">*</span>
          </p>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Show both in create mode; in edit mode show only the selected option */}
            {(!isEditGroup || isMonthly) && (
              <div>
                <RadioButton
                  buttonId="monthly"
                  value="monthly"
                  label="Monthly"
                  isSelected={isMonthly}
                  onChange={() => handlePaymentRequestTypeChange('monthly')}
                  labelStyle="!text-cofeeNeutral10 !font-medium !ml-2"
                  linkLabelToInput
                />
                <div
                  className={`mt-3 ${
                    !isMonthly ? 'pointer-events-none opacity-50' : ''
                  }`}
                >
                  <Input
                    leftIconName="calendar"
                    placeholder="Select billing period"
                    value={selectedMonthLabel}
                    onClick={handleOpenBillingModal}
                    className="w-full"
                    readOnly={!isMonthly}
                  />
                  <div className="mt-2 flex items-start gap-1 text-xs text-cofeeNeutral7">
                    <Icon name="alert-circle" stroke={theme.cofeeGreyBlue500} />
                    <span>
                      Fee will be calculated for attendance marked till the
                      previous day.
                    </span>
                  </div>
                </div>
                {isBillingModalOpen && (
                  <Modal
                    title="Request date"
                    onClose={handleBillingModalCancel}
                    primaryButtonLabel="Apply"
                    primaryButtonClick={handleBillingModalApply}
                    secondaryButtonLabel="Cancel"
                    secondaryButtonClick={handleBillingModalCancel}
                    className="!w-105"
                  >
                    <MonthCard
                      handleActivationDate={(value: string | Date) =>
                        setTempActivationDate(String(value))
                      }
                      selectedDate={tempActivationDate}
                      type={PaymentCollectionType.MONTHLY}
                      handleIntervalFrequency={() => {}}
                    />
                    <AttendancePaymentLinkInfo />
                  </Modal>
                )}
              </div>
            )}

            {(!isEditGroup || isSessionBased) && (
              <div>
                <RadioButton
                  buttonId="session_based"
                  value="session_based"
                  label="After a specific number of classes"
                  isSelected={isSessionBased}
                  onChange={() =>
                    handlePaymentRequestTypeChange('session_based')
                  }
                  labelStyle="!text-cofeeNeutral10 !font-medium !ml-2"
                  linkLabelToInput
                />
                <div
                  className={`mt-3 ${
                    !isSessionBased ? 'pointer-events-none opacity-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {/* replace inpt with Input component */}
                    <Input
                      type="number"
                      min={1}
                      value={formData.sessionsPerCycle ?? ''}
                      onChange={(e) => handleSessionsChange(e.target.value)}
                      placeholder="0"
                      className="w-full"
                      suffixText="Class"
                    />
                  </div>
                  <div className="mt-2 flex items-start gap-1 text-xs text-cofeeNeutral7">
                    <Icon name="alert-circle" stroke={theme.cofeeGreyBlue500} />
                    <span>
                      Payment link is sent automatically the next day once a
                      student completes this many classes.
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <hr className="border-cofeeGreyBlue100" />

        {/* Accept payments as installments */}
        <InstallmentSection
          formData={formData}
          onInputChange={onInputChange}
          isEnabled={false} // Update this true if installment allow for always
        />
      </div>
    </div>
  );
};

export default AttendanceBasedFeeSection;
